/**
 * Authentication Utilities for Admin Panel
 * Handles 401 errors, session cleanup, and user notifications
 */

/**
 * Clear all authentication data from storage
 */
export const clearAuthData = () => {
  const authKeys = [
    "token",
    "id",
    "IsAdmin",
    "IsSuper",
    "AdminType",
    "adminType",
    "user",
    "adminData",
    "userPreferences",
    "lastActivity",
    "refreshToken",
    "sessionData",
  ];

  authKeys.forEach((key) => {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch (e) {
      console.log(`Error clearing ${key}:`, e);
    }
  });

  console.log("🧹 All authentication data cleared");
};

/**
 * Handle 401 Unauthorized error
 * @param {Object} error - The error response
 * @param {string} customMessage - Optional custom message
 */
export const handleUnauthorizedError = (error, customMessage = null) => {
  console.log("🚨 401 Unauthorized - Session expired, redirecting to login");

  // Clear all authentication data
  clearAuthData();

  // Get error message
  const errorMessage =
    customMessage ||
    error.response?.data?.message ||
    error.response?.data?.error ||
    "Your session has expired. Please login again.";

  // Store message to show on login page
  sessionStorage.setItem("loginMessage", errorMessage);
  sessionStorage.setItem("loginMessageType", "warning");

  // Redirect to login page
  window.location.href = "/login";
};

/**
 * Handle 403 Forbidden error
 * @param {Object} error - The error response
 */
export const handleForbiddenError = (error) => {
  console.log("🚨 403 Forbidden - Insufficient permissions");

  const errorMessage =
    error.response?.data?.message ||
    error.response?.data?.error ||
    "You don't have permission to access this resource.";

  // Show error message
  if (typeof window !== "undefined" && window.showError) {
    window.showError(errorMessage);
  } else {
    alert(errorMessage);
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem("token");
  return !!token;
};

/**
 * Get authentication token
 * @returns {string|null}
 */
export const getAuthToken = () => {
  return localStorage.getItem("token");
};

/**
 * Set authentication token
 * @param {string} token
 */
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem("token", token);
  } else {
    localStorage.removeItem("token");
  }
};

/**
 * Logout user and redirect to login
 * @param {string} message - Optional logout message
 */
export const logoutUser = (message = "You have been logged out") => {
  clearAuthData();

  sessionStorage.setItem("loginMessage", message);
  sessionStorage.setItem("loginMessageType", "info");

  window.location.href = "/login";
};

/**
 * Check if token is expired (basic check)
 * @param {string} token - JWT token
 * @returns {boolean}
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.log("Error checking token expiration:", error);
    return true;
  }
};

/**
 * Refresh authentication status
 * @returns {Promise<boolean>}
 */
export const refreshAuthStatus = async () => {
  const token = getAuthToken();

  if (!token || isTokenExpired(token)) {
    handleUnauthorizedError({
      response: { data: { message: "Token expired" } },
    });
    return false;
  }

  return true;
};
